/**
 * SubtitleList Component
 * Editable table/list of all subtitles
 *
 * Performance optimizations:
 * - React.memo on SubtitleRow to prevent row re-renders
 * - useMemo for currentPlayingIndex calculation
 * - useCallback for event handlers
 */

import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import './SubtitleList.css';

interface Subtitle {
  index: number;
  text: string;
  startTime: number;
  endTime: number;
  confidence?: number;
}

interface SubtitleListProps {
  subtitles: Subtitle[];
  selectedIndex: number | null;
  currentTime: number;
  onSubtitleSelect: (index: number) => void;
  onSubtitleTextChange: (index: number, text: string) => void;
  onPlaySegment: (index: number) => void;
  onAddSubtitle?: (afterIndex: number | null) => void;
  onDeleteSubtitle?: (index: number) => void;
  // Per-sentence "re-run AI annotation" — only wired in edit-published-course
  // mode where stale annotations from the live course need refreshing after
  // text changes. Returns a promise so the button can show a spinner.
  onReparseSentence?: (index: number) => Promise<void>;
}

// Format time display - moved outside component to avoid recreation
const formatTime = (seconds: number): string => {
  if (!seconds || !isFinite(seconds)) return '0:00.0';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toFixed(1).padStart(4, '0')}`;
};

// Memoized SubtitleRow component to prevent unnecessary re-renders
interface SubtitleRowProps {
  subtitle: Subtitle;
  isSelected: boolean;
  isPlaying: boolean;
  isEditing: boolean;
  editText: string;
  editInputRef: React.RefObject<HTMLTextAreaElement>;
  onSelect: () => void;
  onStartEditing: () => void;
  onEditTextChange: (text: string) => void;
  onSave: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onPlaySegment: () => void;
  onDelete?: () => void;
  onReparse?: () => void;
  isReparsing?: boolean;
  playSegmentTitle: string;
  deleteSubtitleTitle: string;
  reparseTitle: string;
}

const SubtitleRow = memo<SubtitleRowProps>(({
  subtitle,
  isSelected,
  isPlaying,
  isEditing,
  editText,
  editInputRef,
  onSelect,
  onStartEditing,
  onEditTextChange,
  onSave,
  onKeyDown,
  onPlaySegment,
  onDelete,
  onReparse,
  isReparsing,
  playSegmentTitle,
  deleteSubtitleTitle,
  reparseTitle,
}) => {
  return (
    <div
      data-subtitle-index={subtitle.index}
      className={`subtitle-row ${isSelected ? 'selected' : ''} ${isPlaying ? 'playing' : ''}`}
      onClick={onSelect}
    >
      <div className="subtitle-cell index">{subtitle.index + 1}</div>
      <div className="subtitle-cell text">
        {isEditing ? (
          <textarea
            ref={editInputRef}
            className="edit-textarea"
            value={editText}
            onChange={(e) => onEditTextChange(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={onSave}
            onClick={(e) => e.stopPropagation()}
            rows={2}
          />
        ) : (
          <div
            className="text-content"
            onDoubleClick={(e) => {
              e.stopPropagation();
              onStartEditing();
            }}
          >
            {subtitle.text}
          </div>
        )}
      </div>
      <div className="subtitle-cell time">{formatTime(subtitle.startTime)}</div>
      <div className="subtitle-cell time">{formatTime(subtitle.endTime)}</div>
      <div className="subtitle-cell action">
        <button
          className="play-segment-btn"
          onClick={(e) => {
            e.stopPropagation();
            onPlaySegment();
          }}
          title={playSegmentTitle}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        </button>
        {onReparse && (
          <button
            className="reparse-segment-btn"
            onClick={(e) => {
              e.stopPropagation();
              onReparse();
            }}
            title={reparseTitle}
            disabled={isReparsing}
            aria-busy={isReparsing}
          >
            {isReparsing ? (
              <span className="reparse-spinner" aria-hidden="true" />
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12a9 9 0 1 1-3-6.7" />
                <polyline points="21 3 21 9 15 9" />
              </svg>
            )}
          </button>
        )}
        {onDelete && (
          <button
            className="delete-segment-btn"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            title={deleteSubtitleTitle}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
});

SubtitleRow.displayName = 'SubtitleRow';

const SubtitleList: React.FC<SubtitleListProps> = ({
  subtitles,
  selectedIndex,
  currentTime,
  onSubtitleSelect,
  onSubtitleTextChange,
  onPlaySegment,
  onAddSubtitle,
  onDeleteSubtitle,
  onReparseSentence,
}) => {
  const { t } = useTranslation(['subtitle', 'common']);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [reparsingIndex, setReparsingIndex] = useState<number | null>(null);
  const editInputRef = useRef<HTMLTextAreaElement>(null);

  // Per-row reparse trigger — confirms first (creator pays AI cost), then
  // delegates to the page-level handler, tracking the row's loading state
  // so the rest of the table stays interactive.
  const handleReparse = useCallback(async (index: number) => {
    if (!onReparseSentence) return;
    const confirmed = window.confirm(t('subtitle:list.reparseConfirm'));
    if (!confirmed) return;
    setReparsingIndex(index);
    try {
      await onReparseSentence(index);
    } finally {
      setReparsingIndex((current) => (current === index ? null : current));
    }
  }, [onReparseSentence, t]);

  // Memoized currentPlayingIndex - avoid recalculating on every render
  const currentPlayingIndex = useMemo(() => {
    const current = subtitles.find(
      (s) => currentTime >= s.startTime && currentTime < s.endTime
    );
    return current ? current.index : null;
  }, [subtitles, currentTime]);

  // Start editing - memoized
  const startEditing = useCallback((subtitle: Subtitle) => {
    setEditingIndex(subtitle.index);
    setEditText(subtitle.text);
    // Focus input after render
    setTimeout(() => {
      editInputRef.current?.focus();
      editInputRef.current?.select();
    }, 0);
  }, []);

  // Save editing - memoized
  const saveEditing = useCallback(() => {
    if (editingIndex !== null) {
      onSubtitleTextChange(editingIndex, editText);
      setEditingIndex(null);
      setEditText('');
    }
  }, [editingIndex, editText, onSubtitleTextChange]);

  // Cancel editing
  const cancelEditing = useCallback(() => {
    setEditingIndex(null);
    setEditText('');
  }, []);

  // Handle key press in edit mode
  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      saveEditing();
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  }, [saveEditing, cancelEditing]);

  // Auto-scroll to current subtitle
  useEffect(() => {
    if (selectedIndex !== null) {
      const element = document.querySelector(`[data-subtitle-index="${selectedIndex}"]`);
      if (element) {
        const container = element.closest('.subtitle-list-content');
        if (container) {
          const elementTop = element.getBoundingClientRect().top;
          const containerTop = container.getBoundingClientRect().top;
          const containerHeight = container.clientHeight;
          const elementHeight = (element as HTMLElement).offsetHeight;

          // Check if element is outside visible area
          if (elementTop < containerTop || elementTop + elementHeight > containerTop + containerHeight) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }
    }
  }, [selectedIndex]);

  return (
    <div className="subtitle-list">
      <div className="subtitle-list-header">
        <div className="header-cell index">{t('subtitle:list.headerIndex')}</div>
        <div className="header-cell text">{t('subtitle:list.headerText')}</div>
        <div className="header-cell time">{t('subtitle:list.headerStart')}</div>
        <div className="header-cell time">{t('subtitle:list.headerEnd')}</div>
        <div className="header-cell action">{t('subtitle:list.headerActions')}</div>
      </div>
      <div className="subtitle-list-content">
        {subtitles.map((subtitle) => (
          <SubtitleRow
            key={`subtitle-${subtitle.index}`}
            subtitle={subtitle}
            isSelected={selectedIndex === subtitle.index}
            isPlaying={currentPlayingIndex === subtitle.index}
            isEditing={editingIndex === subtitle.index}
            editText={editText}
            editInputRef={editInputRef}
            onSelect={() => onSubtitleSelect(subtitle.index)}
            onStartEditing={() => startEditing(subtitle)}
            onEditTextChange={setEditText}
            onSave={saveEditing}
            onKeyDown={handleKeyPress}
            onPlaySegment={() => onPlaySegment(subtitle.index)}
            onDelete={onDeleteSubtitle ? () => onDeleteSubtitle(subtitle.index) : undefined}
            onReparse={onReparseSentence ? () => handleReparse(subtitle.index) : undefined}
            isReparsing={reparsingIndex === subtitle.index}
            playSegmentTitle={t('subtitle:list.playSegment')}
            deleteSubtitleTitle={t('subtitle:list.deleteSubtitle')}
            reparseTitle={t('subtitle:list.reparseSentence')}
          />
        ))}
      </div>
      <div className="subtitle-list-footer">
        <span className="footer-info">
          {t('subtitle:list.totalSubtitles', { count: subtitles.length })}
        </span>
        {onAddSubtitle && (
          <button
            className="add-subtitle-btn"
            onClick={() => onAddSubtitle(selectedIndex)}
            title={selectedIndex !== null ? t('subtitle:list.addAfter', { index: selectedIndex + 1 }) : t('subtitle:list.addAtEnd')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
            </svg>
            {t('subtitle:list.addSentence')}
          </button>
        )}
      </div>
    </div>
  );
};

// Wrap the entire component with React.memo for additional optimization
export default memo(SubtitleList);
